import { interpolate } from "./interpolate";
import { Route } from "./Route";

type LegacyAdminRouteMigration = {
    resource: string;
    list: Route;
    create?: Route;
    edit?: (identifier: string) => string;
    details?: (identifier: string) => string;
    allowDetailsSuffixes?: boolean;
    legacyDetailsTab?: string;
    allowListQuery?: boolean;
    defaultToDetails?: boolean;
};

const LEGACY_ADMIN_ROUTE_MIGRATIONS: LegacyAdminRouteMigration[] = [
    {
        resource: "pages",
        list: Route.ADMIN_PAGES,
        create: Route.ADMIN_PAGES_CREATE,
        details: identifier => interpolate(Route.ADMIN_PAGES_DETAILS, { id: identifier }),
        edit: identifier => interpolate(Route.ADMIN_PAGES_EDIT, { id: identifier }),
    },
    {
        resource: "announcements",
        list: Route.ADMIN_ANNOUNCEMENTS,
        create: Route.ADMIN_ANNOUNCEMENTS_CREATE,
        details: identifier => interpolate(Route.ADMIN_ANNOUNCEMENTS_DETAILS, { id: identifier }),
        edit: identifier => interpolate(Route.ADMIN_ANNOUNCEMENTS_EDIT, { id: identifier }),
    },
    {
        resource: "briefs",
        list: Route.ADMIN_BRIEFS,
        details: identifier => interpolate(Route.ADMIN_BRIEFS_DETAILS, { id: identifier }),
        allowDetailsSuffixes: true,
        allowListQuery: false,
    },
    {
        resource: "languages",
        list: Route.LANGUAGES,
        create: Route.LANGUAGES_CREATE,
        edit: identifier => interpolate(Route.LANGUAGES_EDIT, { id: identifier }),
        details: identifier => interpolate(Route.LANGUAGES_DETAILS, { id: identifier }),
        defaultToDetails: true,
    },
    {
        resource: "tags",
        list: Route.TAGS,
        create: Route.TAGS_CREATE,
        edit: identifier => interpolate(Route.TAGS_EDIT, { id: identifier }),
        details: identifier => interpolate(Route.TAGS_DETAILS, { id: identifier }),
        defaultToDetails: true,
        legacyDetailsTab: "translations",
    },
    {
        resource: "categories",
        list: Route.CATEGORIES,
        create: Route.CATEGORIES_CREATE,
        edit: identifier => interpolate(Route.CATEGORIES_EDIT, { id: identifier }),
        details: identifier => interpolate(Route.CATEGORIES_DETAILS, { id: identifier }),
        defaultToDetails: true,
        legacyDetailsTab: "categories",
    },
    {
        resource: "statistics",
        list: Route.STATISTICS,
    },
    {
        resource: "certification_issuers",
        list: Route.CERTIFICATION_ISSUERS,
        create: Route.CERTIFICATION_ISSUERS_CREATE,
        edit: identifier => interpolate(Route.CERTIFICATION_ISSUERS_EDIT, { id: identifier }),
        details: identifier => interpolate(Route.CERTIFICATION_ISSUERS_DETAILS, { id: identifier }),
        defaultToDetails: true,
    },
    {
        resource: "frameworks",
        list: Route.ADMIN_FRAMEWORKS,
        create: Route.ADMIN_FRAMEWORKS_CREATE,
        details: (identifier: string) => interpolate(Route.ADMIN_FRAMEWORKS_DETAILS, { id: identifier }),
        defaultToDetails: true,
        allowDetailsSuffixes: true,
    },
    {
        resource: "skills",
        list: Route.ADMIN_SKILLS,
        details: (identifier: string) => interpolate(Route.ADMIN_SKILLS_DETAILS, { id: identifier }),
        defaultToDetails: true,
        allowDetailsSuffixes: true,
    },
    {
        resource: "skill_domains",
        list: Route.ADMIN_SKILL_DOMAINS,
        details: (identifier: string) => interpolate(Route.ADMIN_SKILL_DOMAINS_DETAILS, { id: identifier }),
        defaultToDetails: true,
        allowDetailsSuffixes: true,
    },
    {
        resource: "skill_blocks",
        list: Route.ADMIN_SKILL_BLOCKS,
        details: (identifier: string) => interpolate(Route.ADMIN_SKILL_BLOCKS_DETAILS, { id: identifier }),
        defaultToDetails: true,
        allowDetailsSuffixes: true,
    },
];

const resolveIdentifier = (encodedIdentifier: string): string | null => {
    try {
        const identifierParts = decodeURIComponent(encodedIdentifier).split("/").filter(Boolean);

        return identifierParts.at(-1) ?? null;
    } catch {
        return null;
    }
};

export const resolveLegacyAdminRoute = (hash: string): string | null => {
    const routeMatch = hash.match(/^#\/([^/?#]+)(?:\/([^/?#]+))?(?:\/([^/?#]+))?((?:\/[^/?#]+)*)(?:\?([^#]*))?$/);
    if (!routeMatch) {
        return null;
    }

    const [, resource, actionOrIdentifier, action, suffix, query] = routeMatch;
    const migration = LEGACY_ADMIN_ROUTE_MIGRATIONS.find(candidate => candidate.resource === resource);
    if (!migration) {
        return null;
    }

    if (!actionOrIdentifier && !action && !suffix) {
        return query === undefined || migration.allowListQuery !== false ? migration.list : null;
    }

    const supportsSuffix =
        migration.allowDetailsSuffixes === true ||
        (migration.legacyDetailsTab !== undefined && suffix === `/${migration.legacyDetailsTab}`);
    if (suffix && !(action === "show" && supportsSuffix)) {
        return null;
    }
    if (query !== undefined) {
        return null;
    }
    if (actionOrIdentifier === "create" && !action) {
        return migration.create ?? null;
    }
    if (!actionOrIdentifier || actionOrIdentifier === "create") {
        return null;
    }

    const identifier = resolveIdentifier(actionOrIdentifier);
    if (!identifier) {
        return null;
    }

    if (!action) {
        return migration.defaultToDetails
            ? (migration.details?.(identifier) ?? null)
            : (migration.edit?.(identifier) ?? null);
    }

    return action === "show" ? (migration.details?.(identifier) ?? null) : null;
};
